import {
  cacheKeys,
  FeatureName,
  isAssetReadOnly,
  MediaPickerComponent,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentAccount,
  useCurrentWorkspace,
  useIsFeatureEnabled,
  useProduct,
  useUpdateAsset,
  useWorkspaceAsset,
  WorkspaceType,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View, ViewStyle } from 'react-native';
import { Text } from 'react-native-paper';
import { array, boolean, string, object as yupObject } from 'yup';
import { basePadding } from '../../styles/spacing';
import { Colors } from '../../themes/colors';
import { Toggle } from '../crop-circle/Components/Toggle';
import {
  ControlledCheckboxItems,
  ControlledTextInput,
  SaveFormBottom,
  SaveFormComponent,
} from '../forms';
import { GroupSelector } from '../group';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { BottomComponent } from '../navigation';
import { AssetTagGallery } from './assetTagGallery';
import { DeleteAsset } from './deleteAsset';
import { EditAsset as EditAssetOld } from './editAsset-old';
import { PinnedActivities } from './pinnedActivities';
import { TagPickerComponent } from './types';
import { useAssetTags } from './useAssetTags';
import { usePinnedActivities } from './usePinnedActivities';

export type EditAssetStyles = {
  container: ViewStyle;
  contentContainer: ViewStyle;
  imageGalleryContainer?: ViewStyle;
};

export type EditAssetProps = {
  assetId: string;
  mediaPicker: MediaPickerComponent;
  tagPicker?: TagPickerComponent;
  saveForm: SaveFormComponent;
  formHasBorder?: boolean;
  styles: StyleSheet.NamedStyles<EditAssetStyles>;
  allowsEditingCover?: boolean;
  bottomBar?: BottomComponent;
  onDeleted: () => Promise<void>;
  onActivityPress?: (activityId: string) => void;
  onCancel?: () => void;
};

export const EditAsset = (props: EditAssetProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <EditAssetBase {...props} />;
  }

  return <EditAssetOld {...props} />;
};

export const EditAssetBase = (props: EditAssetProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const workspace = useCurrentWorkspace(true);
  const isOEM = workspace?.type === WorkspaceType.Oem;
  const { account } = useCurrentAccount();
  const { updateAsset } = useUpdateAsset({
    assetId: props.assetId,
  });
  const { workspaceAsset } = useWorkspaceAsset(props.assetId);

  const isReadOnly = isAssetReadOnly(account, workspaceAsset);

  // Get workspace from workspaceAsset (more reliable)
  const workspaceId = workspaceAsset?.workspace?.id;
  const { groups } = useAllWorkspaceGroups(workspaceId);
  const { createGroup } = useCreateGroup();
  const asset = workspaceAsset?.asset;
  const { product } = useProduct(asset?.product?.id || '');
  const { assetTags } = useAssetTags(props.assetId);
  const { pinnedActivities } = usePinnedActivities(props.assetId);
  const [showAllPinnedActivities, setShowAllPinnedActivities] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [originalFormValues, setOriginalFormValues] = useState<any>(null);

  // Initialize selectedGroups when workspaceAsset changes
  useEffect(() => {
    if (workspaceAsset?.groups) {
      setSelectedGroups(
        workspaceAsset.groups.map((group) => ({
          id: group.id,
          name: group.name,
        }))
      );
    }
  }, [workspaceAsset?.groups]);

  // Store original form values when asset data loads
  useEffect(() => {
    if (asset && workspaceAsset && !originalFormValues) {
      const initialValues = {
        name: asset?.name ?? '',
        coverImageId: asset?.coverImage ? asset?.coverImage.id : undefined,
        mediaIds: asset?.media?.map((media) => media.id),
        description: asset?.description ?? '',
        details: asset?.details ?? '',
        tagIds: assetTags.map((tag) => tag.id),
        groupIds: workspaceAsset?.groups?.map((group) => group.id),
        addons: asset?.addons?.map((addon) => addon.id),
        product: asset?.product?.name ?? '',
        serialNumber: asset?.serialNumber ?? '',
        hideReportAndNoteOptionsAnonymous:
          workspaceAsset?.anonymousOptions?.hideReportAndNote ?? false,
      };
      setOriginalFormValues(initialValues);
    }
  }, [asset, workspaceAsset, assetTags, originalFormValues]);

  const formSchema = yupObject({
    name: string().required(),
    description: string().optional(),
    details: string().optional(),
    coverImageId: string().optional().nullable(),
    mediaIds: array().of(string()).optional().nullable(),
    tagIds: array().of(string()).optional().nullable(),
    groupIds: array().of(string()).optional().nullable(),
    addons: array().of(string()).optional().nullable(),
    product: string().optional().nullable(),
    serialNumber: string().optional().nullable(),
    hideReportAndNoteOptionsAnonymous: boolean().optional(),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      name: asset?.name ?? '',
      coverImageId: asset?.coverImage ? asset?.coverImage.id : undefined,
      mediaIds: asset?.media?.map((media) => media.id),
      description: asset?.description ?? '',
      details: asset?.details ?? '',
      tagIds: assetTags.map((tag) => tag.id),
      groupIds: workspaceAsset?.groups?.map((group) => group.id),
      addons: asset?.addons?.map((addon) => addon.id),
      product: asset?.product?.name ?? '',
      serialNumber: asset?.serialNumber ?? '',
      hideReportAndNoteOptionsAnonymous:
        workspaceAsset?.anonymousOptions?.hideReportAndNote ?? false,
    },
  });

  const addAssetTag = (tag: any) => {
    const tagIds = getValues('tagIds') || [];
    setValue('tagIds', [...tagIds, tag.id], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const deleteAssetTag = (tagId: string) => {
    const tagIds = getValues('tagIds') || [];
    setValue(
      'tagIds',
      tagIds.filter((id) => id !== tagId),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const onSubmit = async (data: any) => {
    const { product, ...submissionData } = data;

    const input: any = {
      ...submissionData,
      groupIds: selectedGroups.map((group) => group.id),
      id: props.assetId,
    };

    try {
      setIsSubmitting(true);

      await (updateAsset as any)(
        { input: input },
        {
          onSettled: async () => {
            reset({}, { keepValues: true });
            await queryClient.invalidateQueries({
              queryKey: cacheKeys('workspaceAsset').all,
            });
          },
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const onResetForm = () => {
    if (originalFormValues) {
      reset(originalFormValues);
      if (workspaceAsset?.groups) {
        setSelectedGroups(
          workspaceAsset.groups.map((group) => ({
            id: group.id,
            name: group.name,
          }))
        );
      }
    }
  };

  const issueTypeTitle = t('asset.editTitle');

  if (!props.bottomBar) {
    return null;
  }

  return (
    <SaveFormBottom
      isDirty={isDirty}
      isValid={isValid && !isSubmitting}
      onSubmit={async () => {
        await handleSubmit(onSubmit)();
      }}
      title={issueTypeTitle}
      saveLabel={t('asset.save')}
      bottomBar={props.bottomBar}
      onCancel={props.onCancel}
      onResetForm={onResetForm}
    >
      <KeyboardSafeView style={props.styles.container}>
        <ScrollView
          scrollEnabled
          showsVerticalScrollIndicator
          style={props.styles.contentContainer}
        >
          <View style={{ paddingBottom: 90 }}>
            {isOEM && (
              <View style={styles.formItem}>
                <View>
                  <Text variant={'titleMedium'} style={styles.formItemTitle}>
                    {t('asset.productType')}
                  </Text>
                </View>
                <ControlledTextInput
                  control={control}
                  name="product"
                  editable={false}
                  useCropCircle
                />
              </View>
            )}
            <View style={styles.formItem}>
              <View>
                <Text variant={'titleMedium'} style={styles.formItemTitle}>
                  {t('asset.name')}
                </Text>
              </View>
              <ControlledTextInput
                control={control}
                placeholder={t('asset.namePlaceholder')}
                name="name"
                editable={!isReadOnly}
                useCropCircle
              />
            </View>
            {!isOEM && (
              <View style={styles.formItem}>
                <View>
                  <Text variant={'titleMedium'} style={styles.formItemTitle}>
                    {t('asset.description')}
                  </Text>
                </View>
                <ControlledTextInput
                  control={control}
                  placeholder={t('asset.descriptionPlaceholder')}
                  textArea={false}
                  name="description"
                  editable={!isReadOnly}
                  useCropCircle
                />
              </View>
            )}
            {!isOEM && (
              <View style={styles.formItem}>
                <View>
                  <Text variant={'titleMedium'} style={styles.formItemTitle}>
                    {t('asset.details')}
                  </Text>
                </View>
                <ControlledTextInput
                  control={control}
                  placeholder={t('asset.detailsPlaceHolder')}
                  textArea={true}
                  name="details"
                  editable={!isReadOnly}
                  autoExpand
                  useCropCircle
                />
              </View>
            )}

            {/* Pinned Activities Section */}
            <PinnedActivities
              assetId={props.assetId}
              pinnedActivities={pinnedActivities}
              showAll={showAllPinnedActivities}
              onViewAllPress={() => setShowAllPinnedActivities(true)}
              onActivityPress={props.onActivityPress}
            />

            {isOEM && (
              <View style={styles.formItem}>
                <View>
                  <Text variant={'titleMedium'} style={styles.formItemTitle}>
                    {t('asset.serialNumber')}
                  </Text>
                </View>
                <ControlledTextInput
                  control={control}
                  name="serialNumber"
                  editable={!isReadOnly}
                  useCropCircle
                />
              </View>
            )}
            {isOEM &&
              product &&
              product.addons &&
              product.addons.length > 0 && (
                <View style={styles.formItem}>
                  <View>
                    <Text variant={'titleMedium'} style={styles.formItemTitle}>
                      {t('asset.addons', { count: asset?.addons?.length || 0 })}
                    </Text>
                  </View>
                  <View style={styles.addonsContainer}>
                    <ControlledCheckboxItems
                      control={control as any}
                      name="addons"
                      options={product.addons
                        .slice()
                        .sort((a: any, b: any) => a.name.localeCompare(b.name))
                        .map((addon: any) => ({
                          label: addon.name,
                          value: addon.id,
                        }))}
                      position="leading"
                      style={styles.checkboxItems}
                      labelStyle={styles.checkboxItemsLabel}
                    />
                  </View>
                </View>
              )}
            {!isReadOnly && (
              <View style={styles.formItem}>
                <View>
                  <Text variant={'titleMedium'} style={styles.formItemTitle}>
                    {t('asset.groups')}
                  </Text>
                </View>
                <GroupSelector
                  selectedGroups={selectedGroups}
                  availableGroups={groups}
                  onGroupsChange={(groups) => {
                    setSelectedGroups(groups);
                    setValue(
                      'groupIds',
                      groups.map((g) => g.id),
                      { shouldDirty: true, shouldValidate: true }
                    );
                  }}
                  onCreateGroup={createGroup}
                  disabled={false}
                  useCropCircle
                />
              </View>
            )}
            <View style={styles.formItem}>
              <View>
                <Text variant={'titleMedium'} style={styles.formItemTitle}>
                  {t('asset.tagCode')}
                </Text>
              </View>
              <AssetTagGallery
                existingAssetTags={assetTags}
                onAssetTagAdded={addAssetTag}
                onAssetTagDeleted={deleteAssetTag}
                tagPicker={props.tagPicker}
                editable={!isReadOnly}
              />
            </View>
            {!isReadOnly && (
              <View style={styles.formItem}>
                <Controller
                  control={control}
                  name="hideReportAndNoteOptionsAnonymous"
                  render={({ field: { value, onChange } }) => (
                    <Toggle
                      value={value}
                      onValueChange={onChange}
                      label={t('asset.hideReportAndNoteOptionsAnonymous')}
                    />
                  )}
                />
              </View>
            )}
            {!isReadOnly && workspaceAsset && (
              <View style={[styles.deleteAssetContainer]}>
                <DeleteAsset
                  workspaceAsset={workspaceAsset}
                  onDeleted={props.onDeleted}
                />
              </View>
            )}
          </View>
        </ScrollView>
      </KeyboardSafeView>
    </SaveFormBottom>
  );
};

const styles = StyleSheet.create({
  addonsContainer: {
    alignSelf: 'flex-start',
    width: 'auto',
    flexDirection: 'column',
    gap: 0,
    margin: 0,
    padding: 0,
  },
  formItem: {
    width: '100%',
    maxWidth: 473,
    marginTop: 4,
    backgroundColor: Colors.white,
    padding: 16,
    borderRadius: 8,
    marginBottom: 6,
  },
  formItemTitle: {
    fontSize: 16,
    lineHeight: 16,
    color: Colors.black,
    marginBottom: 10,
    fontWeight: '700',
    flex: 1, // Allow title to take up available space
  },
  selectionsDropdown: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: Colors.tabInactive,
    borderRadius: 8,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },

  checkboxItems: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  hideReportLabel: {
    paddingLeft: 0,
    textAlign: 'left',
    fontSize: 14,
    lineHeight: 16,
    color: Colors.black,
    fontWeight: '400',
  },
  hideReport: {
    paddingLeft: 0,
  },
  checkboxItemsLabel: {
    fontSize: 16,
    lineHeight: 19,
    color: Colors.black,
    fontWeight: '400',
    textAlign: 'left',
  },
  deleteAssetContainer: {
    paddingHorizontal: basePadding.paddingHorizontal,
    paddingTop: 10,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  viewAllButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: 'transparent',
  },
  viewAllText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.primaryDark,
  },
});
